import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useInbox,
  useTransitionCampaign,
  type InboxItem,
} from "../hooks/useCampaigns.js";
import { ExemptionInbox } from "../components/visits/ExemptionInbox.js";
import { useEmployees } from "../hooks/useEmployees.js";
import { useSegments } from "../hooks/useSegments.js";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function InboxPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useInbox();
  const { data: employees } = useEmployees();
  const { data: segments } = useSegments();

  const employeeName = (id: string) =>
    employees?.find((e) => e.id === id)?.fullName ?? id;
  const segmentName = (id: string) =>
    segments?.find((s) => s.id === id)?.name ?? id;

  if (isError) {
    return (
      <Alert color="red" title={t("common.error")}>
        {t("pages.inbox.errorLoading")}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Title order={2}>{t("pages.inbox.title")}</Title>
      <Text c="dimmed">{t("pages.inbox.subtitleLong")}</Text>

      {isLoading ? (
        <Stack gap="sm">
          <Skeleton height={120} radius="sm" />
          <Skeleton height={120} radius="sm" />
        </Stack>
      ) : !data || data.length === 0 ? (
        <Text c="dimmed" py="md">
          {t("pages.inbox.emptyLong")}
        </Text>
      ) : (
        <Stack gap="sm">
          {data.map((item) => (
            <InboxCard
              key={item.id}
              item={item}
              employeeName={employeeName}
              segmentName={segmentName}
            />
          ))}
        </Stack>
      )}

      <ExemptionInbox />
    </Stack>
  );
}

interface InboxCardProps {
  item: InboxItem;
  employeeName: (id: string) => string;
  segmentName: (id: string) => string;
}

function InboxCard({ item, employeeName, segmentName }: InboxCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const transition = useTransitionCampaign(item.id);
  const [rejectOpen, { open: openReject, close: closeReject }] = useDisclosure(false);
  const [rejectReason, setRejectReason] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleApprove() {
    setErrorMsg(null);
    try {
      await transition.mutateAsync({ to: "active" });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t("pages.inbox.errorApprove"));
    }
  }

  async function handleReject() {
    if (!rejectReason.trim()) {
      setErrorMsg(t("pages.inbox.reasonRequired"));
      return;
    }
    setErrorMsg(null);
    try {
      await transition.mutateAsync({ to: "rejected", reason: rejectReason.trim() });
      closeReject();
      setRejectReason("");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t("pages.inbox.errorReject"));
    }
  }

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" align="flex-start">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="sm">
            <Text
              fw={600}
              size="md"
              style={{ cursor: "pointer" }}
              onClick={() =>
                void navigate({ to: "/campaigns/$id", params: { id: item.id } })
              }
            >
              {item.title}
            </Text>
            <Badge color="yellow" variant="light">
              {t("pages.inbox.submittedStatus")}
            </Badge>
            <Badge color="gray" variant="light">
              {t("pages.inbox.memberCount", { count: item.memberCount })}
            </Badge>
          </Group>

          {item.description && (
            <Text size="sm" lineClamp={2}>
              {item.description}
            </Text>
          )}

          <Group gap="xs" mt={4}>
            {item.targetSegmentIds.map((s) => (
              <Badge key={s} variant="light" color="blue" size="xs">
                {segmentName(s)}
              </Badge>
            ))}
          </Group>

          <Text size="xs" c="dimmed" mt={4}>
            {t("pages.inbox.submittedByAt", {
              name: employeeName(item.initiatorId),
              date: formatDate(item.createdAt),
              responsible: employeeName(item.responsibleId),
            })}
          </Text>
        </Stack>

        <Group gap="xs">
          <Button
            color="green"
            size="sm"
            loading={transition.isPending}
            onClick={handleApprove}
          >
            {t("pages.inbox.approveButton")}
          </Button>
          <Button color="red" variant="light" size="sm" onClick={openReject}>
            {t("pages.inbox.rejectButton")}
          </Button>
        </Group>
      </Group>

      {errorMsg && (
        <Box mt="xs">
          <Alert color="red">{errorMsg}</Alert>
        </Box>
      )}

      <Modal
        opened={rejectOpen}
        onClose={closeReject}
        title={t("pages.inbox.rejectModalTitle")}
      >
        <Stack gap="sm">
          <Text size="sm">
            {t("pages.inbox.rejectModalDescription")}
          </Text>
          <Textarea
            placeholder={t("pages.inbox.rejectReasonPlaceholderQuote")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            autosize
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeReject}>
              {t("common.cancel")}
            </Button>
            <Button color="red" onClick={handleReject} loading={transition.isPending}>
              {t("pages.inbox.rejectButton")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
